import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import StoreX, { store } from '../../redux/oldStore';
import { ServerResponse } from '../../utils/Server';
import { IJudgeEntryData } from './JudgePortalTypes';
import './JudgePortal.css';

const q = (key: string) => StoreX.GetQueryStringValue(key) || '';

const JudgeEntryPage = () => {
  const [data, setData] = useState<IJudgeEntryData | null>(null);
  const [loading, setLoading] = useState(true);
  const [dirty, setDirty] = useState(false);
  const [deleted, setDeleted] = useState(false);
  const [commentsStrengths, setCommentsStrengths] = useState('');
  const [personalNotes, setPersonalNotes] = useState('');
  const [questionValues, setQuestionValues] = useState<Record<number, string>>({});
  const [ruleValues, setRuleValues] = useState<Record<number, string>>({});
  const [ruleTexts, setRuleTexts] = useState<Record<number, string>>({});

  const load = useCallback(() => {
    setLoading(true);
    const payload = {
      id: q('id'),
      evalId: Number(q('evalId') || 0),
      scheduleAssignmentId: Number(q('scheduleAssignmentId') || 0),
      judgeGroupAssignmentId: Number(q('judgeGroupAssignmentId') || 0),
      finals: q('finals') === 'true' || q('finals') === 'True',
      judgeId: q('judgeId') || null,
      mode: q('mode') || null,
    };
    store.server
      .postApi<ServerResponse<IJudgeEntryData>>('../JudgePortal/EvaluationData', payload)
      .then((x) => {
        if (x.Success && x.Value) {
          setData(x.Value);
          if (x.Value.evaluation) {
            setCommentsStrengths(x.Value.evaluation.commentsStrengths || '');
            setPersonalNotes(x.Value.evaluation.personalNotes || '');
            const qv: Record<number, string> = {};
            (x.Value.evaluation.questions || []).forEach((qq) => {
              if (qq.Value != null && qq.Value !== '-1') qv[qq.Id] = String(qq.Value);
            });
            setQuestionValues(qv);
            const rv: Record<number, string> = {};
            const rt: Record<number, string> = {};
            (x.Value.evaluation.rules || []).forEach((r) => {
              if (r.Value === true) rv[r.Id] = 'true';
              else if (r.Value === false) rv[r.Id] = 'false';
              else if (r.Value === null && r.Text === undefined) {
                // leave unset vs NA: if rule was explicitly NA, Value is null with no violation text
              } else if (r.Value === null) rv[r.Id] = 'na';
              if (r.Text) rt[r.Id] = r.Text;
            });
            // Fix NA detection: rules with Value null and not a violation
            (x.Value.evaluation.rules || []).forEach((r) => {
              if (r.Value === null && !r.Text) rv[r.Id] = rv[r.Id] || 'na';
              if (r.Value === false && r.Text) rt[r.Id] = r.Text;
            });
            setRuleValues(rv);
            setRuleTexts(rt);
            setDeleted(!!x.Value.evaluation.deletedAtUtc);
          }
        } else {
          toast.error(x.Message || 'Unable to load evaluation.', { autoClose: false });
          setData(null);
        }
      })
      .catch(() => {
        toast.error('Unable to load evaluation.', { autoClose: false });
        setData(null);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (dirty) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  const labels = data?.labels || {};
  const LHtmlSpan = (key: string, fallback: string) => <span dangerouslySetInnerHTML={{__html: labels[key]||fallback}}></span>;
  const LHtmlDiv = (key: string, fallback: string) => <div dangerouslySetInnerHTML={{__html: labels[key]||fallback}}></div>;
  const L = (key: string, fallback: string) => labels[key]||fallback;

  const buildPayload = (status: string) => {
    if (!data?.entry || !data.judge || !data.evaluation) return null;
    const questions = (data.template?.questionGroups || [])
      .flatMap((g) => g.questions)
      .map((qq) => ({
        Id: qq.id,
        Value: questionValues[qq.id] ?? '-1',
      }));
    return {
      ProjectId: data.entry.projectKey,
      JudgeId: data.judge.id,
      IsFinals: !!data.isFinals,
      EvaluationId: data.evaluation.id,
      JudgeGroupAssignmentId: data.evaluation.judgeGroupAssignmentId,
      ScheduleRoundAssignmentId: data.evaluation.scheduleRoundAssignmentId,
      Status: status,
      Rules: (data.template?.rules || []).map((r) => {
        const raw = ruleValues[r.id];
        if (raw === 'true') return { Id: r.id, Value: true, Text: '', IsMajorRule: !!r.isMajorRule };
        if (raw === 'false')
          return {
            Id: r.id,
            Value: false,
            Text: ruleTexts[r.id] || '',
            IsMajorRule: !!r.isMajorRule,
          };
        // na or unanswered → null
        return { Id: r.id, Value: null, Text: '', IsMajorRule: !!r.isMajorRule };
      }),
      Questions: questions,
      CommentsStrengths: commentsStrengths,
      CommentsImprovements: '',
      PersonalNotes: personalNotes,
    };
  };

  const notifyParent = () => {
    try {
      if (window.opener) window.opener.postMessage('child-done-refresh-me', '*');
    } catch {
      // ignore
    }
  };

  const save = (submit: boolean) => {
    const status = submit
      ? L('statusFinished', 'Finished')
      : L('statusInProgress', 'In Progress');
    const formData = buildPayload(status);
    if (!formData) return;

    store.server
      .postApi<ServerResponse<boolean>>('../JudgePortal/UpdateEvaluation', formData)
      .then((x) => {
        if (x.Success) {
          setDirty(false);
          toast.success(
            submit
              ? L('msgEvalSavedAndViewable', 'Evaluation saved and shared.')
              : L('msgEvalSaved', 'Evaluation saved.')
          );
          notifyParent();
        } else {
          toast.error(x.Message || 'Save failed.', { autoClose: false });
        }
      });
  };

  const saveAndSubmit = () => {
    const formData = buildPayload(L('statusFinished', 'Finished'));
    if (!formData) return;
    let msg = '';
    if ((formData.CommentsStrengths || '').length < 500) {
      msg += L('msgMinComments', 'Public comments must be at least 500 characters. ');
    }
    for (const rule of formData.Rules) {
      if (
        (rule.Value === false && (rule.Text || '').length < 5) ||
        rule.Value === undefined ||
        (ruleValues[rule.Id] === undefined)
      ) {
        // only fail if unanswered (-1 original) or violation without explanation
      }
    }
    for (const rule of data?.template?.rules || []) {
      const raw = ruleValues[rule.id];
      if (raw === undefined || raw === '-1') {
        msg += L('msgExplainRule', 'Please answer all rules and explain any violations. ');
        break;
      }
      if (raw === 'false' && (ruleTexts[rule.id] || '').length < 5) {
        msg += L('msgExplainRule', 'Please explain rule violations. ');
        break;
      }
    }
    for (const qq of (data?.template?.questionGroups || []).flatMap((g) => g.questions)) {
      if (questionValues[qq.id] === undefined || questionValues[qq.id] === '-1') {
        msg += L('msgMissingRequired', 'Please answer all required questions. ');
        break;
      }
    }
    if (msg) {
      toast.warning(msg);
      return;
    }
    if (!window.confirm(L('confirmReady', 'Ready to share this evaluation?'))) return;
    save(true);
  };

  const noShow = () => {
    if (!window.confirm(L('confirmNoShow', 'Mark as no show?'))) return;
    const formData = buildPayload('No Show');
    if (!formData) return;
    store.server
      .postApi<ServerResponse<boolean>>('../JudgePortal/UpdateEvaluation', formData)
      .then((x) => {
        if (x.Success) {
          toast.success(L('msgNoShow', 'Marked as no show.'));
          notifyParent();
          window.close();
        } else {
          toast.error(x.Message || 'Could not mark no show.', { autoClose: false });
        }
      });
  };

  const removeEval = () => {
    if (!window.confirm('Are you sure you want to delete this Eval?')) return;
    const formData = buildPayload('No Show');
    if (!formData) return;
    store.server
      .postApi<ServerResponse<boolean>>('../JudgePortal/DeleteEvaluation', formData)
      .then((x) => {
        if (x.Success) {
          toast.success('Eval Deleted');
          setDeleted(true);
        } else {
          toast.error(x.Message || 'Delete failed.', { autoClose: false });
        }
      });
  };

  const undelete = () => {
    if (!data?.evaluation || !window.confirm('Are you sure you want to undelete this Eval?')) return;
    store.server
      .postApi<ServerResponse<boolean>>('../JudgePortal/UndeleteEvaluation', {
        evaluationId: data.evaluation.id,
      })
      .then((x) => {
        if (x.Success) {
          toast.success('Eval Restored');
          load();
        } else {
          toast.error(x.Message || 'Restore failed.', { autoClose: false });
        }
      });
  };

  const markDirty = () => setDirty(true);

  if (loading) {
    return (
      <div className="main-body-page grow judge-entry-page" data-testid="judge-entry-page">
        <p className="text-muted">Loading evaluation…</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="main-body-page grow judge-entry-page" data-testid="judge-entry-page">
        <div className="alert alert-warning">Evaluation is not available.</div>
      </div>
    );
  }

  if (data.notSupported) {
    return (
      <div className="main-body-page grow judge-entry-page" data-testid="judge-entry-page">
        <h2>Sorry, Evaluation form not found!</h2>
        <p>
          Sorry but online judging is not supported for this category or your category is not
          properly named. Currently only Paper and Website categories are supported.
        </p>
      </div>
    );
  }

  const entry = data.entry!;
  const category = data.category;
  const files = data.files || {};
  const settings = data.settings || {
    historyDayShowJudgesDocLink: false,
    historyDayShowJudgesPerformanceLink: false,
    historyDayShowJudgesExhibitPhoto: false,
  };

  return (
    <div className="main-body-page grow judge-entry-page" data-testid="judge-entry-page">
      {deleted && (
        <div className="eval-deleted-overlay">
          <h1>Eval Deleted</h1>
          The eval has been deleted you can close this page.
        </div>
      )}
      {data.evaluation?.deletedAtUtc && !deleted && (
        <div className="eval-deleted-bar">
          <h1>Eval Deleted!</h1>
          <p>The eval has been deleted.</p>
          {data.isStaff && (
            <button type="button" className="btn btn-warning" onClick={undelete}>
              <i className="fad fa-trash-restore" /> Undelete Eval
            </button>
          )}
        </div>
      )}

      <div className="row">
        <div className="col-sm-9">
          <span style={{ fontSize: 'x-large', fontWeight: 'bold' }}>{entry.title}</span>
        </div>
        <div className="col-sm-3 text-right">
          <span style={{ fontSize: 'x-large', fontWeight: 'bold', color: '#555' }}>
            {data.template?.text}
          </span>
        </div>
      </div>
      <hr />
      <div className="row">
        <div className="col-sm-9">
          {L('category', 'Category:')} <strong>{category?.name}</strong>
        </div>
        <div className="col-sm-3 text-right">
          {L('judge', 'Judge:')} {data.judge?.firstName} {data.judge?.lastName}
        </div>
      </div>

      {entry.entryFilePath && (
        <div>
          {L('paperLink', 'Paper:')}{' '}
          <strong>
            <a
              href={StoreX.BuildUrl(`../File/EntryFile/${entry.projectKey}`)}
              target="_blank"
              rel="noreferrer"
            >
              {L('paperLinkText', 'Open paper')}
            </a>
          </strong>
        </div>
      )}
      {entry.entryUrl && (
        <div>
          {L('websiteLink', 'Website:')}{' '}
          <strong>
            <a
              href={`https://${entry.entryUrl}.NHDWebCentral.org`}
              target="_blank"
              rel="noreferrer"
            >
              {entry.entryUrl}.NHDWebCentral.org
            </a>
          </strong>
        </div>
      )}
      {files.writtenMaterialsKey && (
        <div>
          {L('writtenLink', 'Written Materials:')}{' '}
          <strong>
            <a
              href={StoreX.BuildUrl(`../File/Image/${files.writtenMaterialsKey}`)}
              target="_blank"
              rel="noreferrer"
            >
              {L('openLink', 'Click to Open')}
            </a>
          </strong>
        </div>
      )}
      {files.exhibitPhotoKey && settings.historyDayShowJudgesExhibitPhoto && (
        <div>
          {L('exhibitPhoto', 'Exhibit Photo:')}{' '}
          <strong>
            <a
              href={StoreX.BuildUrl(`../File/Image/${files.exhibitPhotoKey}`)}
              target="_blank"
              rel="noreferrer"
            >
              {L('openLink', 'Click to Open')}
            </a>
          </strong>
        </div>
      )}
      {category?.askForDocumentaryLink &&
        data.links?.documentaryLink &&
        settings.historyDayShowJudgesDocLink && (
          <div>
            {L('docLink', 'Documentary:')}{' '}
            <strong>
              <a href={data.links.documentaryLink} target="_blank" rel="noreferrer">
                Click Here
              </a>
            </strong>
          </div>
        )}
      {category?.askForDocUpload && files.documentaryKey && settings.historyDayShowJudgesDocLink && (
        <div>
          {L('docUpload', 'Documentary Upload:')}{' '}
          <strong>
            <a
              href={StoreX.BuildUrl(`../File/Image/${files.documentaryKey}`)}
              target="_blank"
              rel="noreferrer"
            >
              Click Here
            </a>
          </strong>
        </div>
      )}
      {category?.askForPerformanceLink && data.links?.performanceLink && (
        <div>
          {L('performanceLinkLabel', 'Performance:')}{' '}
          <strong>
            <a href={data.links.performanceLink} target="_blank" rel="noreferrer">
              Click Here
            </a>
          </strong>
        </div>
      )}
      {category?.askForPerformanceUpload && files.performanceKey && (
        <div>
          {L('performanceUpload', 'Performance Upload:')}{' '}
          <strong>
            <a
              href={StoreX.BuildUrl(`../File/Image/${files.performanceKey}`)}
              target="_blank"
              rel="noreferrer"
            >
              Click Here
            </a>
          </strong>
        </div>
      )}
      {category?.askForPodcastUpload && files.podcastKey && (
        <div>
          {L('podcast', 'Podcast:')}{' '}
          <strong>
            <a
              href={StoreX.BuildUrl(`../File/Image/${files.podcastKey}`)}
              target="_blank"
              rel="noreferrer"
            >
              Click Here
            </a>
          </strong>
        </div>
      )}

      <br />
      {data.isReview && <div className="alert alert-info">{L('viewOnly', 'View only')}</div>}
      {!data.isReview && data.judge?.id !== data.currentUserId && (
        <>{LHtmlDiv('viewAndEdit', '<div class="alert alert-info">Viewing/editing for another judge</div>')}</>
      )}
      {data.isFinals && <div className="alert alert-info">{L('finalsRound', 'Finals round')}</div>}

      {!data.isFinals && data.template && (
        <div className="panel panel-info">
          <div className="panel-heading">
            <strong>{L('evaluationHeading', 'Evaluation')}</strong>
          </div>
          <div className="panel-body">
            <div>{LHtmlDiv('evaluationSub', '')}</div>
            <table className="table sixteen-percent">
              <tbody>
                {data.template.questionGroups.map((group) => (
                  <React.Fragment key={group.id}>
                    <tr>
                      <td colSpan={6} className="question-header">
                        <strong>{group.text}</strong>
                      </td>
                    </tr>
                    <tr>
                      <td />
                      <th>{L('exemplary', 'Exemplary')}</th>
                      <th>{L('proficient', 'Proficient')}</th>
                      <th>{L('developing', 'Developing')}</th>
                      <th>{L('novice', 'Novice')}</th>
                      <th>{L('notEvident', 'Not Evident')}</th>
                    </tr>
                    {group.questions.map((qq) => (
                      <tr key={qq.id}>
                        <td>
                          <strong>{qq.text}</strong>
                        </td>
                        {[0, 1, 2, 3, 4].map((val) => (
                          <td key={val}>
                            <div className="input-label-wrapper">
                              <input
                                type="radio"
                                disabled={!!data.isReview}
                                name={`question-${qq.id}`}
                                value={String(val)}
                                checked={questionValues[qq.id] === String(val)}
                                onChange={() => {
                                  setQuestionValues((p) => ({ ...p, [qq.id]: String(val) }));
                                  markDirty();
                                }}
                              />
                              <label>
                                {val < 4
                                  ? qq.options[val] || ''
                                  : L('notEvident', 'Not Evident')}
                              </label>
                            </div>
                          </td>
                        ))}
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>

            <table className="table">
              <tbody>
                <tr>
                  <td className="question-header" colSpan={3}>
                    {L('rulesHeading', 'Rules')}
                  </td>
                </tr>
                {data.template.rules.map((rule) => (
                  <tr key={rule.id}>
                    <td>
                      <label>{rule.text}</label>
                    </td>
                    <td>
                      <label style={{ marginRight: '1em' }}>
                        <input
                          type="radio"
                          disabled={!!data.isReview}
                          name={`rule-${rule.id}`}
                          checked={ruleValues[rule.id] === 'true'}
                          onChange={() => {
                            setRuleValues((p) => ({ ...p, [rule.id]: 'true' }));
                            markDirty();
                          }}
                        />{' '}
                        {L('ruleYes', 'Yes')}
                      </label>
                      <label style={{ marginRight: '1em' }}>
                        <input
                          type="radio"
                          disabled={!!data.isReview}
                          name={`rule-${rule.id}`}
                          checked={ruleValues[rule.id] === 'false'}
                          onChange={() => {
                            setRuleValues((p) => ({ ...p, [rule.id]: 'false' }));
                            markDirty();
                          }}
                        />{' '}
                        {L('ruleNo', 'No')}
                      </label>
                      {rule.ruleType === 'TrueFalseNA' && (
                        <label>
                          <input
                            type="radio"
                            disabled={!!data.isReview}
                            name={`rule-${rule.id}`}
                            checked={ruleValues[rule.id] === 'na'}
                            onChange={() => {
                              setRuleValues((p) => ({ ...p, [rule.id]: 'na' }));
                              markDirty();
                            }}
                          />{' '}
                          {L('ruleNa', 'N/A')}
                        </label>
                      )}
                    </td>
                    <td>
                      {ruleValues[rule.id] === 'false' && (
                        <input
                          type="text"
                          className="form-control"
                          disabled={!!data.isReview}
                          placeholder={L('ruleViolationPlaceholder', 'Explain violation')}
                          value={ruleTexts[rule.id] || ''}
                          onChange={(e) => {
                            setRuleTexts((p) => ({ ...p, [rule.id]: e.target.value }));
                            markDirty();
                          }}
                        />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="panel panel-info">
        <div className="panel-body">
          <div className="form-group">
            <label htmlFor="comment-strengths">{L('publicNotes', 'Public notes')}</label>
            <textarea
              id="comment-strengths"
              className="form-control"
              rows={10}
              disabled={!!data.isReview}
              value={commentsStrengths}
              onChange={(e) => {
                setCommentsStrengths(e.target.value);
                markDirty();
              }}
            />
            <h6 className="pull-right">{commentsStrengths.length} of at least 500</h6>
          </div>
          {!data.isReview && (
            <div className="form-group">
              <label htmlFor="personal-notes">{L('privateNotes', 'Private notes')}</label>
              <textarea
                id="personal-notes"
                className="form-control"
                style={{ backgroundColor: '#ffccd2' }}
                rows={10}
                value={personalNotes}
                onChange={(e) => {
                  setPersonalNotes(e.target.value);
                  markDirty();
                }}
              />
            </div>
          )}
        </div>
      </div>

      {!data.isReview && !data.evaluation?.deletedAtUtc && !deleted && (
        <div className="row">
          <div className="col-sm-6">
            <button type="button" className="btn btn-primary" onClick={() => save(false)}>
              <i className="fa fa-save" /> {LHtmlSpan('save', 'Save')}
            </button>{' '}
            <button type="button" className="btn btn-info" onClick={saveAndSubmit}>
              <i className="fa fa-paper-plane" /> {LHtmlSpan('saveAndShare', 'Save & Share')}
            </button>
          </div>
          <div className="col-sm-6 text-right">
            <button type="button" className="btn btn-default" onClick={noShow}>
              <i className="fad fa-location-slash text-danger" /> {LHtmlSpan('noShow', 'No Show')}
            </button>
          </div>
        </div>
      )}

      {!data.evaluation?.deletedAtUtc &&
        data.isStaff &&
        data.evaluation &&
        data.evaluation.id > 0 &&
        Number(q('evalId') || 0) === data.evaluation.id && (
          <div style={{ marginTop: '1rem' }}>
            <span>Eval Id: {data.evaluation.id}</span>
            <br />
            <button type="button" className="btn btn-default" onClick={removeEval}>
              <i className="fad fa-trash text-danger" /> Delete Eval
            </button>
          </div>
        )}
    </div>
  );
};

export default JudgeEntryPage;
